import { type App } from '~/app';
import { defineAsyncComponent } from 'vue';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Ribbon Bar Component');
    app.vueApp.component('stockland-hub-ribbon-bar', defineAsyncComponent(async () => import("./RibbonBar.vue")));
    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            mountRibbonBar(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Ribbon Bar Component', error);
        });

    return function destroyRibbonBar() {
        unMountRibbonBar(listeners);

        app.logger.debug('[DXA] Ribbon Bar Component destroyed');
    };
};

function mountRibbonBar(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const ribbonBars = rootNode.querySelectorAll<HTMLElement>('.ribbon-bar');
    for (const ribbonBar of ribbonBars) {
        const dropdowns = ribbonBar.querySelectorAll<HTMLElement>('.dropdown');

        handleHalcyonCommunitiesDropdown(ribbonBar, listeners);

        checkCurrentDay(ribbonBar)

        disableDropdownMenuCloseOnclick(ribbonBar)
        closeChangeCentreModal(ribbonBar)

        for (const dropdown of dropdowns) {
            // Showing the menu sub items on hover
            const dropdownHoverTriggers = dropdown.querySelectorAll<HTMLElement>('.dropdown-hover-trigger');

            for (const dropdownHoverTrigger of dropdownHoverTriggers) {
                dropdownHoverTrigger.addEventListener('mouseenter', () => forceShowDropdown(dropdown));
                listeners.push([dropdownHoverTrigger, 'mouseenter', () => forceShowDropdown(dropdown)]);

                dropdown.addEventListener('mouseleave', () => forceCloseDropdown(dropdown));
                listeners.push([dropdown, 'mouseleave', () => forceCloseDropdown(dropdown)]);
            }

            dropdown.addEventListener('show.bs.dropdown', () => toggleDropdownActiveStyles(dropdown));
            listeners.push([dropdown, 'show.bs.dropdown', () => toggleDropdownActiveStyles(dropdown)]);

            dropdown.addEventListener('hide.bs.dropdown', () => toggleDropdownActiveStyles(dropdown));
            listeners.push([dropdown, 'hide.bs.dropdown', () => toggleDropdownActiveStyles(dropdown)]);
        }
    }
}

export function unMountRibbonBar(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}

const handleHalcyonCommunitiesDropdown = (ribbonBar: HTMLElement, listeners:  Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>) => {
    // For Halcyon BU we are not going to use the Bootstrap scripting because popper is static and we can get better control without it
    const businessUnitDropdown = ribbonBar.querySelector<HTMLElement>('.business-unit-dropdown');
    if (businessUnitDropdown) {
        const businessUnitDropdownHoverTrigger = businessUnitDropdown.querySelector<HTMLElement>('.dropdown-hover-trigger');
        const dropdownMenu = businessUnitDropdown.querySelector<HTMLElement>('.dropdown-menu');
        if(businessUnitDropdownHoverTrigger && dropdownMenu){
            const showBUDropdown = () => {
                if (!dropdownMenu?.classList.contains('show')) {
                    dropdownMenu?.classList.add('show');
                    toggleDropdownActiveStyles(businessUnitDropdown);
                }
            }

            const hideBUDropdown = () => {
                if (dropdownMenu?.classList.contains('show')) {
                    dropdownMenu?.classList.remove('show');
                    toggleDropdownActiveStyles(businessUnitDropdown);
                }
            }

            businessUnitDropdown.addEventListener('mouseenter', showBUDropdown);
            businessUnitDropdown.addEventListener('mouseleave', hideBUDropdown);
            businessUnitDropdownHoverTrigger.addEventListener('focus', showBUDropdown);
            businessUnitDropdownHoverTrigger.addEventListener('blur', hideBUDropdown);
            listeners.push(
                [businessUnitDropdown, 'mouseenter', showBUDropdown],
                [businessUnitDropdown, 'mouseleave', hideBUDropdown],
                [businessUnitDropdownHoverTrigger, 'focus', showBUDropdown],
                [businessUnitDropdownHoverTrigger, 'blur', hideBUDropdown]
            );
        }

        const businessUnitSubItems = businessUnitDropdown.querySelectorAll<HTMLElement>('.dropdown-sub-item');
        const businessUnitSubItemsDescriptions =
            businessUnitDropdown.querySelectorAll<HTMLElement>('.business-unit-description');

        // On business unit hover update the description text that is displayed
        const updateBusinessUnitText = (businessUnitSubitem: HTMLElement) => {
            const businessUnitSubItemId = businessUnitSubitem.dataset.buId;

            for (const description of businessUnitSubItemsDescriptions) {
                description.classList.remove('active-businessunit-description');
                description.classList.add('hidden');

                if (description.dataset.buId === businessUnitSubItemId) {
                    description.classList.remove('hidden');
                    description.classList.add('active-businessunit-description');
                }
            }
        };

        for (const businessUnitSubitem of businessUnitSubItems) {
            businessUnitSubitem.addEventListener('mouseover', () => updateBusinessUnitText(businessUnitSubitem));
            listeners.push([businessUnitSubitem, 'mouseover', () => updateBusinessUnitText(businessUnitSubitem)]);
        }
    }
}

// Forces dropdown open, called on mouse enter dropdown menu item event
const forceShowDropdown = (dropdown: Element) => {
    const dropdownMenu = dropdown.querySelector<HTMLElement>('.dropdown-menu');
    if (!dropdownMenu?.classList.contains('show')) {
        const dropdownToggle = dropdown.querySelector<HTMLElement>('.dropdown-toggle');
        dropdownToggle?.click();
    }
};

// Forces dropdown closed, called on mouse leave dropdown event
const forceCloseDropdown = (dropdown: Element) => {
    const dropdownMenu = dropdown.querySelector<HTMLElement>('.dropdown-menu');
    if (dropdownMenu?.classList.contains('show')) {
        const dropdownToggle = dropdown.querySelector<HTMLElement>('.dropdown-toggle');
        dropdownToggle?.click();
    }
};

// Called when the dropdowns are shown or hidden and runs helper functions
const toggleDropdownActiveStyles = (dropdown: HTMLElement) => {
    const dropdownChevron = dropdown.querySelector<HTMLElement>('.dropdown-chevron');
    dropdownChevron?.classList.toggle('rotate-180');

    // For menu items, when dropdown is toggled, toggle active class to add or remove chevron and text colour white for menu items
    dropdown.classList.toggle('active');
    toggleBackdropOpacity();
};

// Toggles background opacity to the main content parts of the page
const toggleBackdropOpacity = () => {
    const pageLayoutContainer = document.querySelectorAll<HTMLElement>('.overlay-container');
    for (const x of pageLayoutContainer) {
        const overlayElement = x?.querySelectorAll<HTMLElement>('.overlay-transition');
        if (overlayElement && overlayElement.length > 0) {
            for (const oElement of overlayElement) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                oElement?.classList.toggle('transition-overlay-60');
            }
        } else {
            const divelem = document.createElement('div');
            divelem.classList.add('overlay-transition');
            divelem.classList.add('transition-overlay-60');
            x?.append(divelem);
        }
    }
};

// For Shopping Centre opening hours dropdown to select current day and highlight it
const checkCurrentDay = (ribbonBar: HTMLElement) =>{
    const openingHoursArray = ribbonBar.querySelectorAll<HTMLElement>('.opening-hours-text');
    if(openingHoursArray){
        const currentDay = new Date().getDay()
        for (const [index, openingHours] of openingHoursArray.entries()) {
            if (index === currentDay - 1) {
                openingHours.classList.add('text-primary-accent-text')
            }            
        }
    }
}

const disableDropdownMenuCloseOnclick = (ribbonBar: HTMLElement) => {
    const dropdownMenus = ribbonBar.querySelectorAll('.dropdown-menu');

    for (const menu of dropdownMenus) {
        menu.addEventListener('click', function(event) {
            event.stopPropagation();
        });
    }
}

const closeChangeCentreModal = (ribbonBar: HTMLElement) => {
    const closeCentreModalButton = ribbonBar.querySelector<HTMLElement>('.close-change-centre-button');
    const centreModal = ribbonBar.querySelector<HTMLElement>('.rtc-change-centre-modal');
    const centreModalButton = ribbonBar.querySelector<HTMLElement>('.change-centre-dropdown-button');
    
    if (closeCentreModalButton && centreModal &&centreModalButton) {
        const iconElement = centreModalButton.querySelector('.icon');
        closeCentreModalButton.addEventListener('click', () => {
            centreModal.classList.remove('show'); 
            centreModal.setAttribute('aria-expanded', 'false'); 
            centreModalButton.classList.remove('show'); 
            centreModalButton.setAttribute('aria-expanded', 'false'); 
            iconElement?.classList.remove('rotate-180');
            toggleBackdropOpacity()
        });
    }
};